import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Columns3, Trash2, Ban, RotateCcw } from 'lucide-react';
import { useInventoryStore, itemHasLocalStock } from '../../store/inventoryStore';
import { useWarehouseStore } from '../../store/warehouseStore';
import { useProjectStore } from '../../store/projectStore';
import InventoryItemDrawer from './InventoryItemDrawer';
import StockUnitDrawer from './StockUnitDrawer';
import FilterSelect from './FilterSelect';
import { conditionBadgeClass } from './movement/shared';
import type { Item } from '../../store/inventoryStore';

interface Props {
  searchQuery: string;
  subView: 'stock' | 'item_types';
  onAddItemRef?: (fn: () => void) => void;
}

// ── Badge helpers ────────────────────────────────────────────────────────────

const statusStyle = (status: string, isDark: boolean) => {
  switch (status) {
    case 'in stock':    return isDark ? 'bg-green-900/40 text-green-400'  : 'bg-green-100 text-green-700';
    case 'checked out': return isDark ? 'bg-amber-900/40 text-amber-400'  : 'bg-amber-100 text-amber-700';
    case 'retired':     return isDark ? 'bg-red-900/40   text-red-400'    : 'bg-red-100   text-red-700';
    default:            return isDark ? 'bg-gray-700     text-gray-300'   : 'bg-gray-100  text-gray-600';
  }
};

/** Fill the table cell (2px inset) without growing row height. `rounded` matches Requests badges. */
const sheetBadgeClass =
  'absolute inset-0.5 flex items-center justify-center text-xs font-semibold rounded px-2 leading-none box-border overflow-hidden whitespace-nowrap';

const SheetBadgeCell: React.FC<{
  colKey: string;
  className?: string;
  style?: React.CSSProperties;
  clickable?: boolean;
  children: React.ReactNode;
}> = ({ colKey, className, style, clickable, children }) => (
  <td key={colKey} className={`p-0 relative align-middle min-w-0 overflow-hidden${clickable ? ' cursor-pointer' : ''}`}>
    <span className={`${sheetBadgeClass}${clickable ? ' cursor-pointer hover:underline' : ''} ${className ?? ''}`} style={style}>
      {children}
    </span>
  </td>
);

const fmtDate = (d: Date) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const STATUS_TABS = [
  { key: 'in stock',    label: 'In Stock' },
  { key: 'checked out', label: 'Checked Out' },
  { key: 'retired',     label: 'Retired' },
  { key: 'all',         label: 'All' },
] as const;
type StatusTab = typeof STATUS_TABS[number]['key'];

/** Fixed stock table layout — columns must not shift when filters change row content */
const STOCK_TABLE_COLUMNS = [
  { key: 'select',       label: '',            width: 40 },
  { key: 'itemName',     label: 'Item Name',   width: '14%' },
  { key: 'unitId',       label: 'Unit ID',     width: 72 },
  { key: 'serialMac',    label: 'Serial/MAC',  width: '11%' },
  { key: 'category',     label: 'Category',    width: '9%' },
  { key: 'storageArea',  label: 'Storage Area', width: '10%' },
  { key: 'condition',    label: 'New/Used',    width: 88 },
  { key: 'status',       label: 'Status',      width: 104 },
  { key: 'reference',    label: 'Reference',   width: '10%' },
  { key: 'owner',        label: 'Owner',       width: '9%' },
  { key: 'lastLog',      label: 'Last Log',    width: 148 },
] as const;

const STOCK_DATA_COLUMNS = STOCK_TABLE_COLUMNS.filter(c => c.key !== 'select');
const VISIBLE_COLUMNS_KEY = 'inventory-visible-columns';

const defaultVisibleColumns = () =>
  new Set(STOCK_DATA_COLUMNS.map(c => c.key));

const loadVisibleColumns = (): Set<string> => {
  try {
    const raw = localStorage.getItem(VISIBLE_COLUMNS_KEY);
    if (raw) {
      const keys = JSON.parse(raw) as string[];
      const valid = keys.filter(k => STOCK_DATA_COLUMNS.some(c => c.key === k));
      if (valid.length > 0) return new Set(valid);
    }
  } catch { /* ignore corrupt storage */ }
  return defaultVisibleColumns();
};

const cellTruncate = 'overflow-hidden text-ellipsis whitespace-nowrap max-w-0';

const ITEM_TABLE_COLUMNS = [
  { key: 'name',        label: 'Item Name' },
  { key: 'sku',         label: 'SKU' },
  { key: 'category',    label: 'Category' },
  { key: 'stockCount',  label: 'Stock Count' },
  { key: 'notes',       label: 'Notes' },
] as const;

type SortDirection = 'asc' | 'desc';

const compareStrings = (a: string | null | undefined, b: string | null | undefined, dir: SortDirection) => {
  const cmp = (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
};

const compareNumbers = (a: number, b: number, dir: SortDirection) =>
  dir === 'asc' ? a - b : b - a;

const ColumnPicker: React.FC<{
  visibleColumns: Set<string>;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  border: string;
  text: string;
  muted: string;
  cardBg: string;
  rowHover: string;
  filterActive: string;
}> = ({ visibleColumns, onToggle, onShowAll, border, text, muted, cardBg, rowHover, filterActive }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const hiddenCount = STOCK_DATA_COLUMNS.length - visibleColumns.size;

  return (
    <div className="relative ml-auto flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center justify-center h-[26px] w-[26px] rounded transition-colors hover:opacity-80"
        style={{ color: hiddenCount > 0 ? filterActive : muted }}
        title="Choose columns"
        aria-label="Choose columns"
      >
        <Columns3 size={14} />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 z-50 rounded-md shadow-xl py-1 min-w-[160px]"
          style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
        >
          <div className="px-2.5 py-1.5 flex items-center justify-between" style={{ borderBottom: `1px solid ${border}` }}>
            <span className="text-[10px] uppercase tracking-wide font-medium" style={{ color: muted }}>Columns</span>
            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={onShowAll}
                className="text-[10px] hover:underline"
                style={{ color: filterActive }}
              >
                Show all
              </button>
            )}
          </div>
          {STOCK_DATA_COLUMNS.map(col => (
            <label
              key={col.key}
              className="flex items-center gap-2 px-2.5 py-1.5 text-xs cursor-pointer transition-colors"
              style={{ color: text }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = rowHover; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
            >
              <input
                type="checkbox"
                checked={visibleColumns.has(col.key)}
                onChange={() => onToggle(col.key)}
                className="rounded"
              />
              {col.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryView: React.FC<Props> = ({ searchQuery, subView, onAddItemRef }) => {
  const { items, stockUnits, isLoadingItems, isLoadingUnits, fetchItems, fetchStockUnits } = useInventoryStore();
  const { defaultWarehouseId, storageAreasByWarehouse } = useWarehouseStore();
  const { appTheme } = useProjectStore();
  const isDark = appTheme === 'dark';

  const [statusTab, setStatusTab]           = useState<StatusTab>('all');
  const [filterItemId, setFilterItemId]     = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterOwner, setFilterOwner]       = useState('');
  const [filterStorageAreaId, setFilterStorageAreaId] = useState('');
  const [filterNew, setFilterNew]           = useState(true);
  const [filterUsed, setFilterUsed]         = useState(true);
  const [selectedIds, setSelectedIds]       = useState<Set<string>>(new Set());
  const [stockSortColumn, setStockSortColumn] = useState<string | null>(null);
  const [stockSortDirection, setStockSortDirection] = useState<SortDirection>('asc');
  const [itemSortColumn, setItemSortColumn] = useState<string | null>(null);
  const [itemSortDirection, setItemSortDirection] = useState<SortDirection>('asc');
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(loadVisibleColumns);

  // Drawer: undefined = closed, null = add mode, Item = edit/view mode
  const [drawerItem, setDrawerItem] = useState<Item | null | undefined>(undefined);
  const [drawerUnitId, setDrawerUnitId] = useState<string | undefined>(undefined);

  const openAdd = useCallback(() => setDrawerItem(null), []);
  useEffect(() => { onAddItemRef?.(openAdd); }, [onAddItemRef, openAdd]);

  useEffect(() => {
    fetchItems();
    if (defaultWarehouseId) fetchStockUnits(defaultWarehouseId);
  }, [defaultWarehouseId, fetchItems, fetchStockUnits]);

  useEffect(() => {
    setFilterStorageAreaId('');
  }, [defaultWarehouseId]);

  // ── Lookup maps ────────────────────────────────────────────────────────────
  const itemById = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  const areasByWarehouse = defaultWarehouseId ? (storageAreasByWarehouse[defaultWarehouseId] ?? []) : [];
  const areaById = useMemo(() => new Map(areasByWarehouse.map(a => [a.id, a])), [areasByWarehouse]);

  // ── Derived filter options ─────────────────────────────────────────────────
  const categories = useMemo(() => {
    const set = new Set(items.map(i => i.category).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [items]);

  const owners = useMemo(() => {
    const set = new Set(stockUnits.map(u => u.owner).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [stockUnits]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { 'in stock': 0, 'checked out': 0, 'retired': 0 };
    stockUnits.forEach(u => { if (u.status in counts) counts[u.status]++; });
    return counts;
  }, [stockUnits]);

  // ── Filtered rows ──────────────────────────────────────────────────────────
  const filteredUnits = useMemo(() => {
    const toTime = (d: Date) => (d instanceof Date ? d : new Date(d)).getTime();

    const filtered = stockUnits.filter(unit => {
      if (statusTab !== 'all' && unit.status !== statusTab) return false;
      if (statusTab !== 'retired' && unit.status === 'retired') return false;
      if (filterItemId && unit.itemId !== filterItemId) return false;
      if (filterOwner && unit.owner !== filterOwner) return false;
      if (filterStorageAreaId && unit.storageAreaId !== filterStorageAreaId) return false;
      if (!filterNew && unit.condition === 'new') return false;
      if (!filterUsed && unit.condition === 'used') return false;
      if (filterCategory) {
        const item = itemById.get(unit.itemId);
        if (!item || item.category !== filterCategory) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const item = itemById.get(unit.itemId);
        const haystack = [
          item?.name, item?.sku, item?.category,
          unit.serialMac, unit.reference, unit.owner, String(unit.unitId),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    const sorted = [...filtered];
    if (stockSortColumn) {
      sorted.sort((a, b) => {
        const itemA = itemById.get(a.itemId);
        const itemB = itemById.get(b.itemId);
        const areaA = a.storageAreaId ? areaById.get(a.storageAreaId) : null;
        const areaB = b.storageAreaId ? areaById.get(b.storageAreaId) : null;
        switch (stockSortColumn) {
          case 'itemName':    return compareStrings(itemA?.name, itemB?.name, stockSortDirection);
          case 'unitId':      return compareNumbers(a.unitId, b.unitId, stockSortDirection);
          case 'serialMac':   return compareStrings(a.serialMac, b.serialMac, stockSortDirection);
          case 'category':    return compareStrings(itemA?.category, itemB?.category, stockSortDirection);
          case 'storageArea': return compareStrings(areaA?.name, areaB?.name, stockSortDirection);
          case 'condition':   return compareStrings(a.condition, b.condition, stockSortDirection);
          case 'status':      return compareStrings(a.status, b.status, stockSortDirection);
          case 'reference':   return compareStrings(a.reference, b.reference, stockSortDirection);
          case 'owner':       return compareStrings(a.owner, b.owner, stockSortDirection);
          case 'lastLog':     return compareNumbers(toTime(a.updatedAt), toTime(b.updatedAt), stockSortDirection);
          default:            return 0;
        }
      });
    } else {
      sorted.sort((a, b) => toTime(b.updatedAt) - toTime(a.updatedAt));
    }
    return sorted;
  }, [stockUnits, statusTab, filterItemId, filterCategory, filterOwner, filterStorageAreaId, filterNew, filterUsed, searchQuery, itemById, areaById, stockSortColumn, stockSortDirection]);

  // ── Selection ──────────────────────────────────────────────────────────────
  const toggleSelect = (id: string) =>
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () =>
    setSelectedIds(selectedIds.size === filteredUnits.length ? new Set() : new Set(filteredUnits.map(u => u.id)));
  const allSelected = filteredUnits.length > 0 && selectedIds.size === filteredUnits.length;

  // ── Colours ────────────────────────────────────────────────────────────────
  const pageBg   = isDark ? '#171a1f' : '#f3f4f6';
  const cardBg   = isDark ? '#20242b' : '#ffffff';
  const border   = isDark ? '#343a43' : '#e5e7eb';
  const headerBg = isDark ? '#20242b' : '#f0f1f3';
  const text     = isDark ? '#e5e7eb' : '#111827';
  const muted    = isDark ? '#9ca3af' : '#6b7280';
  const inputBg  = isDark ? '#171a1f' : '#f9fafb';
  const rowHover = isDark ? '#262b35' : '#f9fafb';
  const filterActive = isDark ? '#60a5fa' : '#2563eb';

  const isLoading = isLoadingItems || isLoadingUnits;
  const showItemsLoading = isLoadingItems && items.length === 0;
  const showStockLoading = isLoading && stockUnits.length === 0;

  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState<string | null>(null);
  const [confirmDiscontinueItemId, setConfirmDiscontinueItemId] = useState<string | null>(null);
  const [hideDiscontinued, setHideDiscontinued] = useState(false);
  const [itemActionBusyId, setItemActionBusyId] = useState<string | null>(null);

  // ── Filtered items (item types view) ─────────────────────────────────────
  const filteredItems = useMemo(() => {
    let result = items;
    if (hideDiscontinued) {
      result = result.filter(i => !i.isDiscontinued);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        [i.name, i.sku, i.category, i.notes].filter(Boolean).join(' ').toLowerCase().includes(q)
      );
    }

    const sorted = [...result];
    if (itemSortColumn) {
      sorted.sort((a, b) => {
        const countA = stockUnits.filter(u => u.itemId === a.id).length;
        const countB = stockUnits.filter(u => u.itemId === b.id).length;
        switch (itemSortColumn) {
          case 'name':       return compareStrings(a.name, b.name, itemSortDirection);
          case 'sku':        return compareStrings(a.sku, b.sku, itemSortDirection);
          case 'category':   return compareStrings(a.category, b.category, itemSortDirection);
          case 'stockCount': return compareNumbers(countA, countB, itemSortDirection);
          case 'notes':      return compareStrings(a.notes, b.notes, itemSortDirection);
          default:           return 0;
        }
      });
    } else {
      sorted.sort((a, b) => {
        // Active items first, then discontinued
        if (a.isDiscontinued !== b.isDiscontinued) return a.isDiscontinued ? 1 : -1;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
    }
    return sorted;
  }, [items, searchQuery, stockUnits, itemSortColumn, itemSortDirection, hideDiscontinued]);

  const handleStockSort = (column: string) => {
    if (stockSortColumn === column) {
      setStockSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setStockSortColumn(column);
      setStockSortDirection('asc');
    }
  };

  const handleItemSort = (column: string) => {
    if (itemSortColumn === column) {
      setItemSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setItemSortColumn(column);
      setItemSortDirection('asc');
    }
  };

  const getSortIcon = (column: string, activeColumn: string | null, direction: SortDirection) => {
    if (activeColumn !== column) return null;
    return direction === 'asc'
      ? <ArrowUp size={12} className={isDark ? 'text-gray-300' : 'text-gray-600'} />
      : <ArrowDown size={12} className={isDark ? 'text-gray-300' : 'text-gray-600'} />;
  };

  const sortableThClass = (isActive: boolean) =>
    `px-3 py-2 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap cursor-pointer transition-colors ${
      isDark
        ? `hover:bg-[#2d333c] hover:text-gray-200 ${isActive ? 'text-gray-200' : ''}`
        : `hover:bg-gray-200 hover:text-gray-900 ${isActive ? 'text-gray-900' : ''}`
    }`;

  const visibleDataColumns = useMemo(
    () => STOCK_DATA_COLUMNS.filter(c => visibleColumns.has(c.key)),
    [visibleColumns],
  );

  const tableColSpan = 1 + visibleDataColumns.length;

  const persistVisibleColumns = (next: Set<string>) => {
    localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(Array.from(next)));
    setVisibleColumns(next);
  };

  const toggleVisibleColumn = (key: string) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 1) return prev;
        next.delete(key);
      } else {
        next.add(key);
      }
      localStorage.setItem(VISIBLE_COLUMNS_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const showAllColumns = () => persistVisibleColumns(defaultVisibleColumns());

  const renderUnitCell = (
    colKey: string,
    unit: typeof stockUnits[number],
    item: Item | undefined,
    area: { name: string } | null | undefined,
  ) => {
    switch (colKey) {
      case 'itemName':
        return (
          <td key={colKey} className={`px-3 py-1.5 ${cellTruncate}`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-1.5 min-w-0 max-w-full">
              <button
                className="text-sm text-blue-400 hover:text-blue-300 hover:underline text-left truncate min-w-0"
                onClick={() => item && setDrawerItem(item)}
              >
                {item?.name ?? <span style={{ color: muted }}>Unknown</span>}
              </button>
              {item?.isDiscontinued && (
                <span title="Discontinued" className="inline-flex flex-shrink-0" aria-label="Discontinued">
                  <Ban
                    size={12}
                    style={{ color: isDark ? '#fdba74' : '#c2410c' }}
                  />
                </span>
              )}
            </div>
          </td>
        );
      case 'unitId':
        return (
          <SheetBadgeCell
            colKey={colKey}
            clickable
            style={{ backgroundColor: isDark ? '#374151' : '#e5e7eb', color: isDark ? '#93c5fd' : '#2563eb' }}
          >
            {unit.unitId}
          </SheetBadgeCell>
        );
      case 'serialMac':
        return (
          <td key={colKey} className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: unit.serialMac ? text : muted }}>
            {unit.serialMac || '(none)'}
          </td>
        );
      case 'category':
        return (
          <td key={colKey} className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: text }}>
            {item?.category || <span style={{ color: muted }}>—</span>}
          </td>
        );
      case 'storageArea':
        return (
          <td key={colKey} className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: area ? text : muted }}>
            {area?.name || '—'}
          </td>
        );
      case 'condition':
        return (
          <SheetBadgeCell colKey={colKey} className={`capitalize ${conditionBadgeClass(unit.condition, isDark)}`}>
            {unit.condition}
          </SheetBadgeCell>
        );
      case 'status':
        return (
          <SheetBadgeCell colKey={colKey} className={`capitalize ${statusStyle(unit.status, isDark)}`}>
            {unit.status}
          </SheetBadgeCell>
        );
      case 'reference':
        return (
          <td key={colKey} className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: unit.reference ? text : muted }}>
            {unit.reference || '—'}
          </td>
        );
      case 'owner':
        return (
          <td key={colKey} className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: text }}>
            {unit.owner || <span style={{ color: muted }}>—</span>}
          </td>
        );
      case 'lastLog':
        return (
          <td key={colKey} className="px-3 py-1.5 text-sm whitespace-nowrap" style={{ color: muted }}>
            {fmtDate(unit.updatedAt)}
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ backgroundColor: pageBg }}>

      {/* ── Item Types view ── */}
      {subView === 'item_types' && (
        <div className="flex-1 min-h-0 px-4 pt-4 pb-4 flex flex-col">
          <div className="flex items-center justify-end mb-2 flex-shrink-0">
            <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: muted }}>
              <input
                type="checkbox"
                checked={hideDiscontinued}
                onChange={e => setHideDiscontinued(e.target.checked)}
                className="rounded w-3.5 h-3.5"
                style={{
                  colorScheme: isDark ? 'dark' : 'light',
                  accentColor: '#3b82f6',
                }}
              />
              Hide discontinued
            </label>
          </div>
          <div className="flex-1 min-h-0 overflow-auto rounded" style={{ border: `1px solid ${border}` }}>
            <table className="w-full">
              <thead className="sticky top-0 z-10" style={{ backgroundColor: headerBg }}>
                <tr style={{ borderBottom: `1px solid ${border}` }}>
                  {ITEM_TABLE_COLUMNS.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleItemSort(col.key)}
                      className={sortableThClass(itemSortColumn === col.key)}
                      style={{ color: itemSortColumn === col.key ? text : muted }}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {getSortIcon(col.key, itemSortColumn, itemSortDirection)}
                      </div>
                    </th>
                  ))}
                  <th
                    className="px-3 py-2 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap"
                    style={{ color: muted, width: 120 }}
                  />
                </tr>
              </thead>
              <tbody style={{ backgroundColor: cardBg }}>
                {showItemsLoading && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-sm" style={{ color: muted }}>Loading…</td></tr>
                )}
                {!showItemsLoading && filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-sm" style={{ color: muted }}>
                      {items.length === 0
                        ? 'No items yet. Use "Create New Item" to add your first item.'
                        : hideDiscontinued
                          ? 'No active items match the search.'
                          : 'No items match the search.'}
                    </td>
                  </tr>
                )}
                {!showItemsLoading && filteredItems.map((item, idx) => {
                  const stockCount = stockUnits.filter(u => u.itemId === item.id).length;
                  const isUsed = itemHasLocalStock(item.id, stockUnits);
                  const isConfirmDelete = confirmDeleteItemId === item.id;
                  const isConfirmDisc = confirmDiscontinueItemId === item.id;
                  const busy = itemActionBusyId === item.id;
                  return (
                    <tr
                      key={item.id}
                      className="group transition-colors"
                      style={{
                        borderBottom: idx < filteredItems.length - 1 ? `1px solid ${border}` : undefined,
                        opacity: item.isDiscontinued ? 0.72 : 1,
                      }}
                      onMouseEnter={e => (e.currentTarget as HTMLElement).style.backgroundColor = rowHover}
                      onMouseLeave={e => (e.currentTarget as HTMLElement).style.backgroundColor = ''}
                    >
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <button
                            onClick={() => setDrawerItem(item)}
                            className="text-sm text-blue-400 hover:text-blue-300 hover:underline text-left font-medium truncate"
                          >
                            {item.name}
                          </button>
                          {item.isDiscontinued && (
                            <span title="Discontinued" className="inline-flex flex-shrink-0" aria-label="Discontinued">
                              <Ban
                                size={12}
                                style={{ color: isDark ? '#fdba74' : '#c2410c' }}
                              />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: item.sku ? text : muted }}>
                        {item.sku || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: item.category ? text : muted }}>
                        {item.category || '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <span
                          className="inline-flex items-center justify-center text-xs font-medium rounded px-1.5 py-0.5 min-w-[28px]"
                          style={{
                            backgroundColor: stockCount > 0 ? (isDark ? '#14532d40' : '#dcfce7') : (isDark ? '#374151' : '#e5e7eb'),
                            color: stockCount > 0 ? (isDark ? '#4ade80' : '#15803d') : muted,
                          }}
                        >
                          {stockCount}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-sm" style={{ color: item.notes ? text : muted }}>
                        {item.notes || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {isConfirmDelete ? (
                          <span className="inline-flex items-center gap-1.5">
                            <button
                              disabled={busy}
                              onClick={async () => {
                                setItemActionBusyId(item.id);
                                const ok = await useInventoryStore.getState().removeItem(item.id);
                                setItemActionBusyId(null);
                                setConfirmDeleteItemId(null);
                                if (!ok) setConfirmDiscontinueItemId(item.id);
                              }}
                              className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors disabled:opacity-50"
                            >
                              Delete
                            </button>
                            <span style={{ color: muted }}>·</span>
                            <button
                              onClick={() => setConfirmDeleteItemId(null)}
                              className="text-xs transition-colors"
                              style={{ color: muted }}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : isConfirmDisc ? (
                          <span className="inline-flex items-center gap-1.5">
                            <button
                              disabled={busy}
                              onClick={async () => {
                                setItemActionBusyId(item.id);
                                await useInventoryStore.getState().setItemDiscontinued(item.id, true);
                                setItemActionBusyId(null);
                                setConfirmDiscontinueItemId(null);
                              }}
                              className="text-xs font-medium transition-colors disabled:opacity-50"
                              style={{ color: isDark ? '#fdba74' : '#c2410c' }}
                            >
                              Discontinue
                            </button>
                            <span style={{ color: muted }}>·</span>
                            <button
                              onClick={() => setConfirmDiscontinueItemId(null)}
                              className="text-xs transition-colors"
                              style={{ color: muted }}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {item.isDiscontinued ? (
                              <button
                                disabled={busy}
                                onClick={async () => {
                                  setItemActionBusyId(item.id);
                                  await useInventoryStore.getState().setItemDiscontinued(item.id, false);
                                  setItemActionBusyId(null);
                                }}
                                className="p-1 rounded hover:bg-green-900/30 disabled:opacity-50"
                                style={{ color: muted }}
                                title="Reactivate"
                              >
                                <RotateCcw size={13} />
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setConfirmDeleteItemId(null);
                                  setConfirmDiscontinueItemId(item.id);
                                }}
                                className="p-1 rounded hover:bg-amber-900/30"
                                style={{ color: muted }}
                                title="Discontinue"
                              >
                                <Ban size={13} />
                              </button>
                            )}
                            {!isUsed && !item.isDiscontinued && (
                              <button
                                onClick={() => {
                                  setConfirmDiscontinueItemId(null);
                                  setConfirmDeleteItemId(item.id);
                                }}
                                className="p-1 rounded hover:bg-red-900/30"
                                style={{ color: muted }}
                                title="Delete"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Stock (Inventory) view ── */}
      {subView === 'stock' && <>

      {/* Status filter tabs */}
      <div className="flex items-center gap-1.5 px-4 pt-3 pb-0 flex-shrink-0">
        {STATUS_TABS.map(tab => {
          const count = tab.key === 'all' ? stockUnits.length : statusCounts[tab.key] ?? 0;
          const isActive = statusTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setStatusTab(tab.key)}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors"
              style={{
                backgroundColor: isActive ? (isDark ? '#374151' : '#1f2937') : 'transparent',
                color: isActive ? '#ffffff' : muted,
                border: `1px solid ${isActive ? (isDark ? '#4b5563' : '#374151') : border}`,
              }}
            >
              {tab.label}
              <span
                className="px-1.5 py-px rounded-full text-[10px] font-semibold"
                style={{
                  backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : (isDark ? '#374151' : '#e5e7eb'),
                  color: isActive ? '#fff' : muted,
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="px-4 pt-3 pb-0 flex-shrink-0">
        <div
          className="flex items-center gap-3 flex-nowrap px-3 py-1.5 rounded"
          style={{ backgroundColor: cardBg, border: `1px solid ${border}` }}
        >
          {/* All Items */}
          <div className="flex items-center gap-1.5">
            <span
              className="text-xs font-medium"
              style={{ color: filterItemId ? filterActive : muted }}
            >
              Item
            </span>
            <FilterSelect
              value={filterItemId}
              onChange={setFilterItemId}
              placeholder="All Items"
              options={items.map(i => ({ value: i.id, label: i.name }))}
              minWidth={140}
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
              activeColor={filterActive}
              panelBg={cardBg}
              rowHover={rowHover}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: border }} />

          {/* Category */}
          <div className="flex items-center gap-1.5">
            <span
              className="text-xs font-medium"
              style={{ color: filterCategory ? filterActive : muted }}
            >
              Category
            </span>
            <FilterSelect
              value={filterCategory}
              onChange={setFilterCategory}
              placeholder="All Categories"
              options={categories.map(c => ({ value: c, label: c }))}
              minWidth={130}
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
              activeColor={filterActive}
              panelBg={cardBg}
              rowHover={rowHover}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: border }} />

          {/* Owner */}
          <div className="flex items-center gap-1.5">
            <span
              className="text-xs font-medium"
              style={{ color: filterOwner ? filterActive : muted }}
            >
              Owner
            </span>
            <FilterSelect
              value={filterOwner}
              onChange={setFilterOwner}
              placeholder="All Owners"
              options={owners.map(o => ({ value: o, label: o }))}
              minWidth={120}
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
              activeColor={filterActive}
              panelBg={cardBg}
              rowHover={rowHover}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: border }} />

          {/* Storage Area */}
          <div className="flex items-center gap-1.5">
            <span
              className="text-xs font-medium"
              style={{ color: filterStorageAreaId ? filterActive : muted }}
            >
              Storage Area
            </span>
            <FilterSelect
              value={filterStorageAreaId}
              onChange={setFilterStorageAreaId}
              placeholder="All Areas"
              options={areasByWarehouse.map(a => ({ value: a.id, label: a.name }))}
              minWidth={130}
              inputBg={inputBg}
              border={border}
              text={text}
              muted={muted}
              activeColor={filterActive}
              panelBg={cardBg}
              rowHover={rowHover}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: border }} />

          {/* Condition */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium" style={{ color: muted }}>Condition</span>
            <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ color: text }}>
              <input type="checkbox" checked={filterNew} onChange={e => setFilterNew(e.target.checked)} className="w-3.5 h-3.5" />
              New
            </label>
            <label className="flex items-center gap-1.5 text-xs font-medium cursor-pointer" style={{ color: text }}>
              <input type="checkbox" checked={filterUsed} onChange={e => setFilterUsed(e.target.checked)} className="w-3.5 h-3.5" />
              Used
            </label>
          </div>

          <ColumnPicker
            visibleColumns={visibleColumns}
            onToggle={toggleVisibleColumn}
            onShowAll={showAllColumns}
            border={border}
            text={text}
            muted={muted}
            cardBg={cardBg}
            rowHover={rowHover}
            filterActive={filterActive}
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 px-4 pt-3 pb-4 flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto rounded" style={{ border: `1px solid ${border}` }}>
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: 40 }} />
              {visibleDataColumns.map(col => (
                <col key={col.key} style={{ width: typeof col.width === 'number' ? `${col.width}px` : col.width }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10" style={{ backgroundColor: headerBg }}>
              <tr style={{ borderBottom: `1px solid ${border}` }}>
                <th className="px-3 py-1.5 text-left align-middle">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                {visibleDataColumns.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleStockSort(col.key)}
                    className={sortableThClass(stockSortColumn === col.key)}
                    style={{ color: stockSortColumn === col.key ? text : muted }}
                  >
                    <div className="flex items-center gap-1">
                      {col.label}
                      {getSortIcon(col.key, stockSortColumn, stockSortDirection)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody style={{ backgroundColor: cardBg }}>
              {showStockLoading && (
                <tr>
                  <td colSpan={tableColSpan} className="px-3 py-10 text-center text-sm" style={{ color: muted }}>Loading…</td>
                </tr>
              )}
              {!showStockLoading && filteredUnits.length === 0 && (
                <tr>
                  <td colSpan={tableColSpan} className="px-3 py-10 text-center text-sm" style={{ color: muted }}>
                    {stockUnits.length === 0
                      ? items.length === 0
                        ? 'No items yet. Use "+ Add Item" to create your first item.'
                        : 'No stock units yet.'
                      : 'No units match the current filters.'}
                  </td>
                </tr>
              )}
              {!showStockLoading && filteredUnits.map((unit, idx) => {
                const item = itemById.get(unit.itemId);
                const area = unit.storageAreaId ? areaById.get(unit.storageAreaId) : null;
                const isSelected = selectedIds.has(unit.id);
                return (
                  <tr
                    key={unit.id}
                    className="transition-colors"
                    style={{
                      borderBottom: idx < filteredUnits.length - 1 ? `1px solid ${border}` : undefined,
                      backgroundColor: isSelected ? (isDark ? '#1d2535' : '#eff6ff') : undefined,
                    }}
                    onClick={() => setDrawerUnitId(unit.id)}
                    onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = rowHover; }}
                    onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = ''; }}
                  >
                    <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(unit.id)} />
                    </td>
                    {visibleDataColumns.map(col => renderUnitCell(col.key, unit, item, area))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      </>}

      {/* Item drawer — shared by both views */}
      {drawerItem !== undefined && (
        <InventoryItemDrawer
          item={drawerItem}
          onClose={() => setDrawerItem(undefined)}
          onSaved={() => fetchItems()}
        />
      )}

      {/* Unit detail drawer */}
      {drawerUnitId && (
        <StockUnitDrawer
          unitId={drawerUnitId}
          onClose={() => setDrawerUnitId(undefined)}
        />
      )}
    </div>
  );
};

export default InventoryView;
